import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getCourse, updateCourse, deleteCourse, setCoursePublished } from '../../api/courses';
import { getLessons } from '../../api/lessons';
import { getQuizzesByCourse } from '../../api/quizzes';
import { useAuth } from '../../context/AuthContext';
import { unwrapList, extractErrorMessage } from '../../utils/api';
import { isAccountAdmin } from '../../utils/scope';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import CourseModules from '../CourseModules/CourseModules';
import CourseLessons from '../CourseLessons/CourseLessons';
import CourseQuizzes from '../CourseQuizzes/CourseQuizzes';
import CourseEnrollments from '../CourseEnrollments/CourseEnrollments';
import CourseExams from '../CourseExams/CourseExams';
import CourseRevenue from '../CourseRevenue/CourseRevenue';
import CourseAnalytics from '../CourseAnalytics/CourseAnalytics';
import SkillsPrerequisitesTab from '../Courses/SkillsPrerequisitesTab';
import CourseCategories from '../CourseCategories/CourseCategories';
import CourseGames from '../CourseGames/CourseGames';
import CourseVocabulary from '../CourseVocabulary/CourseVocabulary';
import CourseCoverPicker from '../../components/CourseCoverPicker/CourseCoverPicker';
import CourseTariffs from '../../components/CourseTariffs/CourseTariffs';
import CourseCertificates from '../CourseCertificates/CourseCertificates';
import { invalidateCache } from '../../api/client';
import { useI18n } from '../../i18n/I18nContext';
import { currencyLabel, formatMoney } from '../../utils/datetime';
import './CourseOverview.css';

// Redesign 2026 — 5 logical workspace tabs (was 9 unordered ones).
const TABS = [
  { key: 'curriculum', labelKey: 'tabCurriculum', icon: 'book' },
  { key: 'students', labelKey: 'tabStudents', icon: 'users' },
  { key: 'revenue', labelKey: 'tabRevenue', icon: 'dollar' },
  { key: 'analytics', labelKey: 'tabAnalytics', icon: 'chart' },
  { key: 'certificates', i18nKey: 'certs.tab', icon: 'award' },
  { key: 'settings', labelKey: 'tabSettings', icon: 'gear' },
];

const TAB_ICONS = {
  award: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.48 12.89 17 22l-5-3-5 3 1.52-9.11"/></svg>,
  book: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  users: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  dollar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  chart: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  gear: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
};

// Section-header icons for the Settings tab (visual anchoring per section).
const S_ICON = {
  info: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  image: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>,
  signal: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="20" x2="6" y2="14"/><line x1="12" y1="20" x2="12" y2="8"/><line x1="18" y1="20" x2="18" y2="4"/></svg>,
  tag: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  alert: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

// Curriculum sub-categories — rendered as pills, not tabs.
const CURRICULUM_CATS = [
  { key: 'lessons', labelKey: 'catLessons' },
  { key: 'modules', labelKey: 'catModules' },
  { key: 'tests', labelKey: 'catTests' },
  { key: 'games', labelKey: 'catGames' },
  { key: 'vocabulary', labelKey: 'catVocabulary' },
  { key: 'exams', labelKey: 'catExams' },
];

function CurriculumTab({ courseId, sub, onSubChange }) {
  const { t } = useI18n();
  const active = CURRICULUM_CATS.some((c) => c.key === sub) ? sub : 'lessons';
  return (
    <div className="curriculum-tab">
      <div className="curriculum-pills" role="tablist" aria-label={t('courseOverview.curriculumTypeAriaLabel')}>
        {CURRICULUM_CATS.map((cat) => (
          <button
            key={cat.key}
            type="button"
            role="tab"
            aria-selected={active === cat.key}
            className={`curriculum-pill ${active === cat.key ? 'active' : ''}`}
            onClick={() => onSubChange(cat.key)}
          >
            {t(`courseOverview.${cat.labelKey}`)}
          </button>
        ))}
      </div>
      <div className="curriculum-body">
        {active === 'lessons' && <CourseLessons courseId={courseId} />}
        {active === 'modules' && <CourseModules courseId={courseId} />}
        {active === 'tests' && <CourseQuizzes courseId={courseId} />}
        {active === 'games' && <CourseGames courseId={courseId} />}
        {active === 'vocabulary' && <CourseVocabulary courseId={courseId} />}
        {active === 'exams' && <CourseExams courseId={courseId} />}
      </div>
    </div>
  );
}

function formatPrice(course, t) {
  const n = Number(course?.price || 0);
  if (!n) return t('courseOverview.free');
  return formatMoney(n, course.currency); // "119 000 so'm", never "119,000 UZS"
}

const LEVEL_LABEL_KEYS = {
  BEGINNER: 'ui.levelBeginner',
  INTERMEDIATE: 'ui.levelIntermediate',
  ADVANCED: 'ui.levelAdvanced',
};
function formatLevel(level, t) {
  const key = LEVEL_LABEL_KEYS[String(level || 'BEGINNER').toUpperCase()];
  return key ? t(key) : level;
}

// The create page allows 100; a course named there must stay savable here.
const TITLE_MAX = 100;
const DESC_MAX = 500;

const LEVELS = [
  { value: 'BEGINNER', labelKey: 'levelBeginner', subKey: 'levelBeginnerSub' },
  { value: 'INTERMEDIATE', labelKey: 'levelIntermediate', subKey: 'levelIntermediateSub' },
  { value: 'ADVANCED', labelKey: 'levelAdvanced', subKey: 'levelAdvancedSub' },
];

function SettingsTab({ course, onChange, onDelete }) {
  const { t } = useI18n();
  const initial = {
    title: course.title || '',
    description: course.description || '',
    level: course.level || 'BEGINNER',
    price: course.price || 0,
    thumbnail: course.thumbnail || '',
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  // With active tariffs the backend sets the course price to the cheapest one, so the single
  // price field becomes read-only (editing it would be overwritten on the next tariff save).
  const [hasActiveTariffs, setHasActiveTariffs] = useState(false);
  const handleTariffsLoaded = useCallback(
    (list) => setHasActiveTariffs(list.some((tier) => tier.isActive)),
    []
  );
  const refreshCoursePrice = useCallback(async () => {
    invalidateCache(`*/courses/admin/${course.id}*`);
    try {
      const fresh = await getCourse(course.id);
      if (fresh) {
        onChange({ price: fresh.price });
        setForm((prev) => ({ ...prev, price: fresh.price }));
      }
    } catch {
      /* the tariff list itself is already fresh; the header price catches up on reload */
    }
  }, [course.id, onChange]);

  const isFree = Number(form.price) === 0;
  const dirty =
    form.title !== initial.title ||
    form.description !== initial.description ||
    form.level !== initial.level ||
    Number(form.price) !== Number(initial.price) ||
    (form.thumbnail || '') !== (initial.thumbnail || '');

  const titleTrimmed = form.title.trim();
  const titleValid = titleTrimmed.length > 0 && titleTrimmed.length <= TITLE_MAX;
  const canSave = dirty && titleValid && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const updated = await updateCourse(course.id, {
        title: titleTrimmed,
        description: form.description.trim() || undefined,
        level: form.level,
        price: Number(form.price) || 0,
        thumbnail: form.thumbnail || undefined,
      });
      onChange(updated);
      setMsg(t('courseOverview.settingsSaveSuccess'));
      setTimeout(() => setMsg(null), 2200);
    } catch (e) {
      setError(e?.code === 403
        ? t('courseOverview.noAccess')
        : extractErrorMessage(e, t('courseOverview.settingsSaveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initial);
    setError(null);
    setMsg(null);
  };

  return (
    <div className="settings-panel">
      {/* Section: Basic info */}
      <section className="settings-section">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--info">{S_ICON.info}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.settingsBasicInfoTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.settingsBasicInfoSub')}</p>
          </div>
        </header>

        <div className="settings-grid">
          <label className="settings-field settings-field-full">
            <div className="settings-label-row">
              <span>{t('courseOverview.settingsCourseTitleLabel')} <em>*</em></span>
              <span className={`settings-counter ${form.title.length > TITLE_MAX ? 'is-over' : ''}`}>
                {t('courseOverview.charCounter', { count: form.title.length, max: TITLE_MAX })}
              </span>
            </div>
            <input
              type="text"
              value={form.title}
              maxLength={TITLE_MAX + 20}
              placeholder={t('courseOverview.settingsTitlePlaceholder')}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              aria-invalid={!titleValid}
            />
            {!titleValid && form.title.length > 0 && (
              <span className="settings-help is-error">
                {form.title.length > TITLE_MAX ? t('courseOverview.settingsTitleTooLong', { max: TITLE_MAX }) : t('courseOverview.settingsTitleRequired')}
              </span>
            )}
          </label>

          <label className="settings-field settings-field-full">
            <div className="settings-label-row">
              <span>{t('courseOverview.settingsDescriptionLabel')}</span>
              <span className={`settings-counter ${form.description.length > DESC_MAX ? 'is-over' : ''}`}>
                {t('courseOverview.charCounter', { count: form.description.length, max: DESC_MAX })}
              </span>
            </div>
            <textarea
              rows={4}
              value={form.description}
              maxLength={DESC_MAX + 50}
              placeholder={t('courseOverview.settingsDescriptionPlaceholder')}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <span className="settings-help">{t('courseOverview.settingsDescriptionHelp')}</span>
          </label>
        </div>
      </section>

      {/* Section: Course cover */}
      <section className="settings-section">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--image">{S_ICON.image}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.settingsCoverTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.settingsCoverSub')}</p>
          </div>
        </header>
        <CourseCoverPicker
          value={form.thumbnail}
          onChange={(url) => setForm((f) => ({ ...f, thumbnail: url }))}
          initialTitle={form.title}
          courseTitle={form.title}
        />
      </section>

      {/* Section: Difficulty (segmented pills) */}
      <section className="settings-section">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--signal">{S_ICON.signal}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.settingsDifficultyTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.settingsDifficultySub')}</p>
          </div>
        </header>

        <div className="settings-pills" role="radiogroup" aria-label={t('courseOverview.settingsDifficultyTitle')}>
          {LEVELS.map((lv) => (
            <button
              type="button"
              role="radio"
              aria-checked={form.level === lv.value}
              key={lv.value}
              className={`settings-pill ${form.level === lv.value ? 'is-active' : ''}`}
              onClick={() => setForm({ ...form, level: lv.value })}
            >
              <span className="settings-pill-label">{t(`courseOverview.${lv.labelKey}`)}</span>
              <span className="settings-pill-sub">{t(`courseOverview.${lv.subKey}`)}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Section: Pricing */}
      <section className="settings-section">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--tag">{S_ICON.tag}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.settingsPricingTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.settingsPricingSub')}</p>
          </div>
        </header>

        <div className="settings-grid">
          <div className="settings-field">
            <span className="settings-label">{t('courseOverview.settingsPriceLabel')}</span>
            <div className={`settings-price-input ${isFree || hasActiveTariffs ? 'is-disabled' : ''}`}>
              <input
                type="number"
                min={0}
                step={1000}
                value={isFree ? '' : form.price}
                placeholder="0"
                disabled={isFree || hasActiveTariffs}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
              <span className="settings-price-suffix">{currencyLabel(course.currency)}</span>
            </div>
            <span className="settings-help">
              {hasActiveTariffs
                ? t('courseOverview.settingsPriceFromTariffs')
                : isFree ? t('courseOverview.settingsPriceFreeHelp') : t('courseOverview.settingsPricePaidHelp')}
            </span>
          </div>

          <div className="settings-field">
            <span className="settings-label">{t('courseOverview.settingsMakeFreeLabel')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={isFree}
              disabled={hasActiveTariffs}
              className={`settings-switch ${isFree ? 'is-on' : ''}`}
              onClick={() => setForm({ ...form, price: isFree ? Math.max(50000, Number(initial.price) || 50000) : 0 })}
            >
              <span className="settings-switch-track" aria-hidden="true">
                <span className="settings-switch-thumb" />
              </span>
              <span className="settings-switch-text">{isFree ? t('courseOverview.free') : t('courseOverview.switchPaid')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section: Tariffs — saved one by one, independent of the save bar below */}
      <section className="settings-section">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--tag">{S_ICON.tag}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.tariffsTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.tariffsSub')}</p>
          </div>
        </header>
        <CourseTariffs
          courseId={course.id}
          currency={course.currency || 'UZS'}
          onTariffsLoaded={handleTariffsLoaded}
          onCourseChange={refreshCoursePrice}
        />
      </section>

      {/* Inline status messages */}
      {error && <div className="settings-banner is-error" role="alert">{error}</div>}
      {msg && <div className="settings-banner is-ok" role="status">{msg}</div>}

      {/* Sticky save bar — appears when there are unsaved changes */}
      <div className={`settings-savebar ${dirty ? 'is-visible' : ''}`}>
        <span className="settings-savebar-text">
          {dirty ? t('courseOverview.savebarDirty') : t('courseOverview.savebarClean')}
        </span>
        <div className="settings-savebar-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={handleReset}
            disabled={!dirty || saving}
          >
            {t('courseOverview.discard')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? t('courseOverview.saving') : t('courseOverview.saveChanges')}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <section className="settings-section settings-danger">
        <header className="settings-section-head">
          <span className="settings-section-icon settings-section-icon--alert">{S_ICON.alert}</span>
          <div>
            <h3 className="settings-section-title">{t('courseOverview.dangerZoneTitle')}</h3>
            <p className="settings-section-sub">{t('courseOverview.dangerZoneSub')}</p>
          </div>
        </header>

        <div className="settings-danger-row">
          <div className="settings-danger-info">
            <strong>{t('courseOverview.deleteThisCourse')}</strong>
            <span>{t('courseOverview.deleteThisCourseDesc')}</span>
          </div>
          <button type="button" className="btn-danger" onClick={onDelete}>
            {t('courseOverview.deleteCourseBtn')}
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * Loading placeholder that mirrors the real workspace layout (hero card with
 * a cover block, two text lines and a pill row, plus the tab bar) so nothing
 * flashes unstyled while the course is fetched. Built from the shared
 * LoadingState skeleton primitives (`.ls-skel-shimmer` etc.).
 */
function CourseOverviewSkeleton() {
  const { t } = useI18n();
  return (
    <div className="course-overview">
      <div className="ls-skel-wrap" role="status" aria-live="polite" aria-busy="true">
        <div className="overview-hero">
          <div className="overview-cover">
            <div className="ls-skel-shimmer ov-skel-cover-fill" />
          </div>
          <div className="overview-hero-body">
            <div className="overview-hero-head">
              <div className="ls-skel-pill ls-skel-shimmer" style={{ width: 110 }} />
            </div>
            <div className="ls-skel-line ls-skel-shimmer" style={{ width: '52%', height: 24, marginBottom: 12 }} />
            <div className="overview-meta">
              <div className="ls-skel-pill ls-skel-shimmer" style={{ width: 76, height: 22 }} />
              <div className="ls-skel-pill ls-skel-shimmer" style={{ width: 92, height: 22 }} />
              <div className="ls-skel-pill ls-skel-shimmer" style={{ width: 84, height: 22 }} />
            </div>
            <div className="overview-statrow">
              {[0, 1, 2].map((i) => (
                <div key={i} className="ov-stat">
                  <div className="ls-skel-line ls-skel-shimmer" style={{ width: 34, height: 20, marginBottom: 4 }} />
                  <div className="ls-skel-line ls-skel-shimmer" style={{ width: 58, height: 9 }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="overview-tabs">
          {TABS.map((tb) => (
            <span key={tb.key} className="ov-skel-tab">
              <span className="ls-skel-pill ls-skel-shimmer" style={{ width: 78, height: 14 }} />
            </span>
          ))}
        </div>

        <div className="overview-panel ov-skel-panel">
          <span className="ds-spinner" />
        </div>

        <span className="visually-hidden">{t('courseOverview.loading')}</span>
      </div>
    </div>
  );
}

function CourseOverview() {
  const { t } = useI18n();
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = TABS.some((tb) => tb.key === searchParams.get('tab')) ? searchParams.get('tab') : 'curriculum';
  const curriculumSub = searchParams.get('c') || 'lessons';

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    if (key !== 'curriculum') next.delete('c');
    setSearchParams(next, { replace: true });
  };
  const setCurriculumSub = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'curriculum');
    next.set('c', key);
    setSearchParams(next, { replace: true });
  };

  const [course, setCourse] = useState(null);
  const [stats, setStats] = useState({ lessons: 0, tests: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);

  const loadCourse = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, lessons, quizzes] = await Promise.all([
        getCourse(courseId),
        getLessons(courseId).catch(() => []),
        getQuizzesByCourse(courseId).catch(() => []),
      ]);
      setCourse(c);
      const lessonArr = unwrapList(lessons);
      const quizArr = unwrapList(quizzes);
      const testsTotal = quizArr.reduce((sum, q) => sum + (q.questions?.length ?? 0), 0);
      setStats({ lessons: lessonArr.length, tests: testsTotal });
    } catch (e) {
      // The API client rejects { message, code } where code is the HTTP status (there is no e.response).
      const status = e?.code;
      if (status === 403) setError(t('courseOverview.noAccess'));
      else if (status === 404) setError(t('courseOverview.notFound'));
      else setError(extractErrorMessage(e, t('courseOverview.loadFailed')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const isMine = Boolean(user?.id && course?.createdById === user.id);
  // Publishing is an admin's call; the backend refuses it from anyone else.
  const canPublish = isAccountAdmin(user);

  const handleTogglePublish = async () => {
    if (!course || publishBusy || !canPublish) return;
    setPublishBusy(true);
    setError(null);
    try {
      const updated = await setCoursePublished(course.id, !course.isPublished);
      setCourse((prev) => ({ ...prev, isPublished: updated?.isPublished ?? !prev.isPublished }));
    } catch (e) {
      setError(e?.code === 403
        ? t('courseOverview.noAccess')
        : extractErrorMessage(e, t('courseOverview.publishStatusFailed')));
    } finally {
      setPublishBusy(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteCourse(course.id);
      navigate('/courses');
    } catch (e) {
      setError(e?.code === 403
        ? t('courseOverview.noAccess')
        : extractErrorMessage(e, t('courseOverview.deleteFailed')));
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return <CourseOverviewSkeleton />;
  }

  if (error && !course) {
    return (
      <div className="course-overview">
        <PageEmptyState
          variant="error"
          size="lg"
          title={error}
          actionLabel={t('courseOverview.backToCourses')}
          onAction={() => navigate('/courses')}
        />
      </div>
    );
  }

  return (
    <div className="course-overview">
      <div className="overview-hero">
        <div className="overview-cover">
          {course.thumbnail
            ? <img src={course.thumbnail} alt="" className="overview-cover-img" />
            : <span className="overview-cover-fallback" aria-hidden="true">{(course.title || '?').charAt(0).toUpperCase()}</span>}
        </div>

        <div className="overview-hero-body">
          <div className="overview-hero-head">
            {/* The shell's top bar already shows "Courses / Course workspace";
                a second breadcrumb inside the hero was redundant. */}
            {canPublish && (
              <div className="overview-actions">
                <button
                  type="button"
                  className={course.isPublished ? 'btn-secondary' : 'btn-primary'}
                  onClick={handleTogglePublish}
                  disabled={publishBusy}
                >
                  {publishBusy ? '…' : course.isPublished ? t('courseOverview.unpublish') : t('courseOverview.publishCourse')}
                </button>
              </div>
            )}
            {/* Only an admin publishes: everyone else is told who does, instead of finding no button. */}
            {!canPublish && !course.isPublished && (
              <p className="overview-publish-note">{t('courseOverview.publishByAdmin')}</p>
            )}
          </div>

          <h1 className="overview-title" title={course.title}>{course.title}</h1>

          <div className="overview-meta">
            <span className={`status-chip ${course.isPublished ? 'live' : 'draft'}`}>
              {course.isPublished ? t('courseOverview.statusPublished') : t('courseOverview.statusDraft')}
            </span>
            <span className="meta-pill">{formatLevel(course.level, t)}</span>
            <span className="meta-pill meta-pill-price">{formatPrice(course, t)}</span>
            {isMine && <span className="owner-chip">{t('courseOverview.ownedByYou')}</span>}
          </div>

          <div className="overview-statrow">
            <div className="ov-stat">
              <span className="ov-stat-num">{stats.lessons}</span>
              <span className="ov-stat-lbl">{t('courseOverview.statLessons')}</span>
            </div>
            <div className="ov-stat">
              <span className="ov-stat-num">{stats.tests}</span>
              <span className="ov-stat-lbl">{t('courseOverview.statTestQuestions')}</span>
            </div>
            <div className="ov-stat">
              <span className="ov-stat-num ov-stat-status">{course.isPublished ? t('courseOverview.statusLive') : t('courseOverview.statusDraft')}</span>
              <span className="ov-stat-lbl">{t('courseOverview.statStatus')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* A failed publish or delete (a failed load returns the error card above instead) */}
      {error && <div className="settings-banner is-error" role="alert">{error}</div>}

      <nav className="overview-tabs" role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={tab === tb.key}
            className={`tab ${tab === tb.key ? 'tab-active' : ''}`}
            onClick={() => setTab(tb.key)}
          >
            <span className="tab-icon">{TAB_ICONS[tb.icon]}</span>
            {t(tb.i18nKey || `courseOverview.${tb.labelKey}`)}
          </button>
        ))}
      </nav>

      <section className="overview-panel">
        {tab === 'curriculum' && (
          <CurriculumTab courseId={courseId} sub={curriculumSub} onSubChange={setCurriculumSub} />
        )}
        {tab === 'students' && <CourseEnrollments courseId={courseId} embedded />}
        {tab === 'revenue' && <CourseRevenue courseId={courseId} />}
        {tab === 'analytics' && <CourseAnalytics courseId={courseId} />}
        {tab === 'certificates' && <CourseCertificates courseId={courseId} embedded />}
        {tab === 'settings' && (
          <div className="settings-stack">
            <SettingsTab
              course={course}
              onChange={(c) => setCourse((prev) => ({ ...prev, ...c }))}
              onDelete={() => setConfirmDelete(true)}
            />
            <div className="settings-advanced">
              <h3 className="settings-advanced-title">{t('courseOverview.categoriesHeading')}</h3>
              <CourseCategories courseId={courseId} />
            </div>
            <div className="settings-advanced">
              <h3 className="settings-advanced-title">{t('courseOverview.skillsPrerequisitesHeading')}</h3>
              <SkillsPrerequisitesTab courseId={courseId} />
            </div>
          </div>
        )}
      </section>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>{t('courseOverview.deleteModalTitle')}</h3></div>
            <div className="modal-body">
              <p>{t('courseOverview.deleteModalBody', { title: course.title })}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setConfirmDelete(false)}>{t('courseOverview.cancel')}</button>
              <button type="button" className="btn-danger" onClick={handleDelete}>{t('courseOverview.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseOverview;
